export const hasApensado = (data, next, complete) => {
  return Boolean(data?.apensado || next?.apensado || complete?.apensado);
};

export const isParentApensadoProcess = (data, next, complete) => {
  const isParent = (item) => item?.apensado === true && !item?.apensado_id;

  return Boolean(isParent(data) || isParent(next) || isParent(complete));
};

// Planos que, por enquanto, se comportam exatamente como o Essencial.
// O "2026" (novo modelo de vendas, 22/09/2026) nasceu assim e será
// diferenciado depois — espelha SalesScope::PLANOS_COMO_ESSENCIAL na API.
// O "Análise" (22/10/2026) também entra: o cliente apostila por conta
// própria e paga as custas da emissão.
export const PLANOS_COMO_ESSENCIAL = ["ESSENTIAL", "PLAN_2026", "PLAN_ANALISE"];

export const isPlanoComoEssencial = (familyPlan) =>
  PLANOS_COMO_ESSENCIAL.includes(familyPlan);

// No plano 2026 a taxa da conservatória é paga pelo cliente — o card de
// Despacho muda por causa disso.
export const isPlano2026 = (familyPlan) => familyPlan === "PLAN_2026";

// Plano Análise (22/10/2026): o cliente contrata só a análise documental.
// Documentos digitais (foto/PDF) pela Central, sem Correios, sem
// procuração; laudo em até 7 dias após o último documento.
export const isPlanoAnalise = (familyPlan) => familyPlan === "PLAN_ANALISE";

export const isPlanoAnaliseProcess = (process) =>
  isPlanoAnalise(process?.sale?.family_plan) || !!process?.sale?.is_plano_analise;

// Etapas próprias do plano Análise (process_steps).
export const ETAPAS_ANALISE = {
  documentos: "analise_documentos",
  emAndamento: "analise_em_andamento",
  laudo: "analise_laudo",
  aguardandoContinuacao: "analise_aguardando_continuacao",
};

export const isEtapaAnalise = (stepName) =>
  Object.values(ETAPAS_ANALISE).includes(stepName);

// Links de pagamento da taxa da conservatória (PayPal), por tipo de
// processo. Menor de idade não paga taxa; homologações (e o que mais não
// tiver link) continuam acertando com a equipe pelo WhatsApp.
export const LINKS_TAXA_CONSERVATORIA = {
  filhoOuNeto: "https://www.paypal.com/ncp/payment/39TLRCRMJE4AL",
  aquisicao: "https://www.paypal.com/ncp/payment/3VRDWWTAEQSNA",
  transcricao: "https://www.paypal.com/ncp/payment/PURXCUQ9RC78N",
};

export const taxaConservatoria = (tipoProcesso) => {
  const tipo = String(tipoProcesso || "").toLowerCase();
  if (/menor/.test(tipo)) return { modo: "isento" };
  if (/aquisi/.test(tipo)) {
    return { modo: "link", link: LINKS_TAXA_CONSERVATORIA.aquisicao, rotulo: "Aquisição" };
  }
  if (/transcri/.test(tipo)) {
    return { modo: "link", link: LINKS_TAXA_CONSERVATORIA.transcricao, rotulo: "Transcrição de casamento" };
  }
  if (/atribui|neto|pulo de gera/.test(tipo)) {
    return { modo: "link", link: LINKS_TAXA_CONSERVATORIA.filhoOuNeto, rotulo: "Filho maior ou neto" };
  }
  return { modo: "equipe" };
};

// Selo de apostila de cada documento, pelo nome: só a procuração dispensa a
// apostila. No Essencial/2026 quem apostila é o cliente; nos demais, a CJ.
export const seloApostila = (nomeDocumento, familyPlan) => {
  if (/procura[çc][ãa]o/i.test(nomeDocumento || "")) {
    return { tipo: "nao", texto: "Não precisa de apostila" };
  }
  // Plano Análise: apostilar antes da análise é opcional; o que não vier
  // apostilado fica para a continuação.
  if (isPlanoAnalise(familyPlan)) {
    return { tipo: "opcional", texto: "Apostila: agora ou depois" };
  }
  if (isPlanoComoEssencial(familyPlan)) {
    return { tipo: "sim", texto: "Precisa de apostila" };
  }
  return { tipo: "cj", texto: "A CJ apostila por você" };
};

// Dias úteis (seg–sex) entre a data prevista e hoje. 0 se ainda não passou.
export const diasUteisDeAtraso = (dataPrevista) => {
  if (!dataPrevista) return 0;
  const [ano, mes, dia] = String(dataPrevista).slice(0, 10).split("-").map(Number);
  if (!ano || !mes || !dia) return 0;

  const cursor = new Date(ano, mes - 1, dia);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  let dias = 0;
  while (cursor < hoje) {
    cursor.setDate(cursor.getDate() + 1);
    const semana = cursor.getDay();
    if (semana !== 0 && semana !== 6) dias += 1;
  }
  return dias;
};

// O aviso "em no máximo 2 dias úteis" só vale logo depois do prazo. Passado
// isso, o card troca a promessa por um texto sem prazo (a previsão já não
// representa a realidade e repetir "2 dias" por meses perde a confiança).
export const ATRASO_TOLERADO_DIAS_UTEIS = 2;

export const isAtrasoLongo = (dataPrevista) =>
  diasUteisDeAtraso(dataPrevista) > ATRASO_TOLERADO_DIAS_UTEIS;

// Quando o Airtable registra a chegada de um documento com o reference_id de
// outro modelo de lista (ex.: "RG, CNH" x "RG, CIN, CNH"), a API cria um
// documento novo em vez de atualizar o existente, e o card mostra o mesmo
// documento duas vezes com status diferentes. Aqui fica um por nome + de quem
// é, mantendo o de status mais avançado (na ordem em que apareceu primeiro).
const PESO_STATUS_DOCUMENTO = {
  ARRIVED_SUCCESSFULLY: 3,
  ARRIVED_WRONG: 2,
  NOT_REQUIRED: 1,
  PENDING: 0,
};

const chaveDocumento = (doc) =>
  [
    String(doc?.name || "")
      .replace(/,\s*CIN\b/i, "")
      .trim()
      .toLowerCase(),
    String(doc?.from_who || "").trim().toLowerCase(),
  ].join("|");

export const documentosSemDuplicata = (documentos) => {
  if (!Array.isArray(documentos)) return documentos;

  const escolhido = new Map();
  documentos.forEach((doc) => {
    const chave = chaveDocumento(doc);
    const atual = escolhido.get(chave);
    const peso = PESO_STATUS_DOCUMENTO[doc?.status] ?? 0;
    const pesoAtual = atual ? PESO_STATUS_DOCUMENTO[atual?.status] ?? 0 : -1;
    if (peso > pesoAtual) escolhido.set(chave, doc);
  });

  const vistos = new Set();
  return documentos.reduce((lista, doc) => {
    const chave = chaveDocumento(doc);
    if (vistos.has(chave)) return lista;
    vistos.add(chave);
    lista.push(escolhido.get(chave));
    return lista;
  }, []);
};

export const isEssentialPlanProcess = (data, next, complete) => {
  return [data, next, complete].some((item) =>
    isPlanoComoEssencial(item?.sale?.family_plan)
  );
};
